import productService from '../services/product.service'
import userService from '../services/user.service';
import supplierService from '../services/supplier.service';
import categoryService from '../services/category.service';
import customerService from '../services/customer.service';
import wharehouseService from '../services/wharehouse.service';
import purchaseService from '../services/purchase.service';
import personelService from '../services/personel.service';
import reportsService from '../services/reports.service';
import settingsService from '../services/settings.service';
import { isEnabled as offlineEnabled, cache as offlineCache, cached as offlineCached } from '../helpers/offline';

// In-flight request registry — prevents duplicate simultaneous API calls
const _pending = {};

function once(key, fn) {
    if (_pending[key]) return _pending[key];
    _pending[key] = fn().finally(() => { delete _pending[key]; });
    return _pending[key];
}

export const getProducts = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => productService.get_products()
        .then(response => {
            commit('get_products', response.product);
            // Keep a copy for offline use (only when the feature is on).
            if (offlineEnabled()) offlineCache('products', response.product);
            return response;
        })
        .catch(async (err) => {
            // Offline + feature enabled → hydrate the catalog from the local cache
            // so the POS still works without a connection.
            if (offlineEnabled() && !(err && err.response)) {
                const cachedProducts = await offlineCached('products');
                if (cachedProducts && cachedProducts.length) { commit('get_products', cachedProducts); return; }
            }
            throw err;
        });
    if (force) return fetch();
    if (state.products && state.products.length > 0) return Promise.resolve();
    return once('products', fetch);
}

export const get_low_stock = ({ commit }) => {
    return productService.get_low_stock().then(response => {
        commit('set_low_stock', response.products);
    });
}

export const get_product = ({ commit }, id) => {
    return productService.get_product(id).then(response => {
        commit('get_product', response.data);
    });
}

export const get_settings = ({ commit, state }, { force = false } = {}) => {
    if (!force && state.settings && Object.keys(state.settings).length > 0) return Promise.resolve();
    return once('settings', () =>
        settingsService.get_settings().then(response => {
            commit('get_settings', response.settings ?? {});
        })
    );
}

export const get_users = ({ commit }) => {
    return userService.getAllUser().then(
        response => { commit('get_users', response.data); },
        error => { console.log(error); }
    );
}

export const get_suppliers = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => supplierService.get_suppliers().then(response => {
        commit('get_suppliers', response.data.suppliers);
    });
    if (force) return fetch();
    if (state.suppliers && state.suppliers.length > 0) return Promise.resolve();
    return once('suppliers', fetch);
}

export const get_categories = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => categoryService.get_categories().then(response => commit('get_categories', response.data.data));
    if (force) return fetch();
    if (state.categories && state.categories.length > 0) return Promise.resolve();
    return once('categories', fetch);
}

// Alias used by some pages
export const getCategories = get_categories;

export const get_expense_category = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => categoryService.get_expense_category().then(response => {
        commit('get_expense_category', response.data.data);
    });
    if (force) return fetch();
    if (state.expense_category && state.expense_category.length > 0) return Promise.resolve();
    return once('expense_category', fetch);
}

export const get_expenses = ({ commit }) => {
    return categoryService.get_expenses().then(response => {
        commit('get_expenses', response.data.expenses);
    });
}

export const get_personels = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => personelService.get_personels().then(response => {
        commit('get_personels', response.data.personels);
    });
    if (force) return fetch();
    if (state.personels && state.personels.length > 0) return Promise.resolve();
    return once('personels', fetch);
}

export const get_departments = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => personelService.get_departments().then(response => {
        commit('get_departments', response.data.departments);
    });
    if (force) return fetch();
    if (state.departments && state.departments.length > 0) return Promise.resolve();
    return once('departments', fetch);
}

export const get_customers = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => customerService.get_customerlist().then(response => commit('get_customers', response.data.customers));
    if (force) return fetch();
    if (state.customers && state.customers.length > 0) return Promise.resolve();
    return once('customers', fetch);
}

export const get_wharehouses = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => wharehouseService.get_wharehouses().then(response => {
        commit('get_wharehouses', response.data.data);
    });
    if (force) return fetch();
    if (state.wharehouses && state.wharehouses.length > 0) return Promise.resolve();
    return once('wharehouses', fetch);
}

export const get_pos_warehouses = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => wharehouseService.get_pos_warehouses().then(response => {
        commit('get_pos_warehouses', response.data.data);
    });
    if (force) return fetch();
    if (state.pos_warehouses && state.pos_warehouses.length > 0) return Promise.resolve();
    return once('pos_warehouses', fetch);
}

export const get_transfers = ({ commit }, params = {}) => {
    return wharehouseService.get_transfers(params).then(response => {
        commit('get_transfers', response.data.data);
    });
}

export const wharehouse_items = ({ commit }) => {
    return wharehouseService.wharehouse_items().then(response => {
        commit('wharehouse_items', response.data.data);
    });
}

export const get_purchases = ({ commit, state }, { force = false } = {}) => {
    const fetch = () => purchaseService.get_purchases().then(response => commit('get_purchases', response.data.purchases));
    if (force) return fetch();
    if (state.purchases && state.purchases.length > 0) return Promise.resolve();
    return once('purchases', fetch);
}

export const main_chart = ({ commit }) => {
    return reportsService.get_graph().then(response => {
        commit('get_mainchart', response.data.data);
    });
}
